import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useLayoutEffect,
} from "react";
import {
  prepareWithSegments,
  layoutNextLineRange,
  materializeLineRange,
} from "@chenglou/pretext";
import "./App.css";
import BackgroundShader from "./BackgroundShader";
import BrushOverlay from "./BrushOverlay";
import rabbitSprite from "./assets/rabbit_sprite.png";
import vibrateLogoWebp from "./assets/logo-mark-176.webp";
import vibrateLogoPng from "./assets/logo-mark-176.png";
import servyLogo from "./assets/servy.png";
import gearroomLogo from "./assets/gearroom.webp";
import mfjLogo from "./assets/mfj.png";
import metaLogo from "./assets/meta-icon.webp";
import { MAILTO, PAGES, pageForPath, canonicalFor } from "./meta.mjs";

const CLIENTS = [
  { name: "Meta", logo: metaLogo },
  { name: "Servy", logo: servyLogo },
  { name: "The Gear Room", logo: gearroomLogo },
  { name: "MFJ", logo: mfjLogo },
];

const FRAME_WIDTH = 55;
const FRAME_HEIGHT = 74;
const SHEET_COLS = 4;
const TOTAL_FRAMES = 23;
const VIBRATE_LOGO_W = 202;
const VIBRATE_LOGO_H = 176;
const VIBRATE_LOGO_ASPECT = VIBRATE_LOGO_W / VIBRATE_LOGO_H;

const TITLE_TEXT = "Currently building Vibrate";
const TITLE_LINES = ["Currently building", "Vibrate"];
const TITLE_FONT_FAMILY =
  "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, system-ui, sans-serif";

const FOOTER_LINKS = [
  {
    href: "https://x.com/getvibrate",
    label: "X",
    ariaLabel: "Vibrate on X",
  },
  {
    href: "https://www.instagram.com/getvibrate/",
    label: "Instagram",
    ariaLabel: "Vibrate on Instagram",
  },
];

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    if (mq.addEventListener) {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    }
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("reduce-motion", reduced);
    return () => document.documentElement.classList.remove("reduce-motion");
  }, [reduced]);

  return reduced;
}

function useAnimatedFavicon(reducedMotion) {
  useEffect(() => {
    const staticHref = `${process.env.PUBLIC_URL || ""}/rabbit-favicon-single.png`;
    const getFaviconLink = () => {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      return link;
    };

    if (reducedMotion) {
      getFaviconLink().href = staticHref;
      return undefined;
    }

    const canvas = document.createElement("canvas");
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    ctx.imageSmoothingEnabled = false;

    const img = new Image();
    img.src = rabbitSprite;

    let frame = 0;
    let direction = 1;
    let lastTime = 0;
    let rafId;

    const tick = (time) => {
      if (time - lastTime >= 150) {
        lastTime = time;
        const col = frame % SHEET_COLS;
        const row = Math.floor(frame / SHEET_COLS);
        ctx.clearRect(0, 0, 64, 64);
        ctx.drawImage(
          img,
          col * FRAME_WIDTH, row * FRAME_HEIGHT, FRAME_WIDTH, FRAME_HEIGHT,
          0, 0, 64, 64
        );
        getFaviconLink().href = canvas.toDataURL("image/png");

        let next = frame + direction;
        if (next >= TOTAL_FRAMES - 1) {
          direction = -1;
          next = TOTAL_FRAMES - 1;
        } else if (next <= 0) {
          direction = 1;
          next = 0;
        } else if (direction === -1 && frame === 15) {
          next = 7;
        }
        frame = next;
      }
      rafId = requestAnimationFrame(tick);
    };

    img.onload = () => {
      rafId = requestAnimationFrame(tick);
    };

    return () => cancelAnimationFrame(rafId);
  }, [reducedMotion]);
}

function useElementWidth(ref) {
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(el.getBoundingClientRect().width);
    update();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return width;
}

function useFontsReady() {
  const [ready, setReady] = useState(
    typeof document !== "undefined" && !!document.fonts?.status === false
      ? true
      : document?.fonts?.status === "loaded"
  );

  useEffect(() => {
    if (!document.fonts) {
      setReady(true);
      return;
    }
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return ready;
}

function usePageMeta(page) {
  useEffect(() => {
    if (!page) return undefined;
    document.title = page.title;
    const setContent = (selector, value) => {
      const el = document.head.querySelector(selector);
      if (el) el.setAttribute("content", value);
    };
    setContent('meta[name="description"]', page.description);
    setContent('meta[property="og:title"]', page.title);
    setContent('meta[property="og:description"]', page.description);
    setContent('meta[property="og:url"]', canonicalFor(page));
    setContent('meta[name="twitter:title"]', page.title);
    setContent('meta[name="twitter:description"]', page.description);
    const canonical = document.head.querySelector("link[rel='canonical']");
    if (canonical) canonical.setAttribute("href", canonicalFor(page));
    return undefined;
  }, [page]);
}

/**
 * Runs pretext layout over TITLE_TEXT, using a narrower width for lines
 * whose vertical range overlaps the hero mark's bounding box (shrink-wrap).
 */
function useWrappedTitle(containerWidth, fontsReady) {
  return useMemo(() => {
    // During prerender we deliberately stay in the plain-text fallback branch.
    // The client also renders that branch on its very first pass (width is 0
    // and fonts are not ready yet), so hydration matches. It also gives
    // crawlers a clean <h1> instead of aria-hidden positioned line fragments.
    if (typeof window !== "undefined" && window.__PRERENDER__) return null;
    if (!containerWidth || !fontsReady) return null;

    const fontSize = Math.max(36, Math.min(76, containerWidth * 0.1));
    const lineHeight = fontSize * 1.05;
    const fontWeight = 700;
    const letterSpacingEm = -0.04;
    const letterSpacingPx = letterSpacingEm * fontSize;

    // Hero mark fits into ~3 lines vertically, never larger than the source asset.
    const logoHeight = Math.min(VIBRATE_LOGO_H, lineHeight * 3);
    const logoWidth = logoHeight * VIBRATE_LOGO_ASPECT;
    const logoGutter = fontSize * 0.45;
    const logoTotal = logoWidth + logoGutter;

    const fontSpec = `${fontWeight} ${fontSize}px ${TITLE_FONT_FAMILY}`;
    const lines = [];
    let y = 0;

    // Each TITLE_LINES segment is laid out independently so the break between
    // them is forced regardless of viewport width; within each segment, pretext
    // wraps based on the available width (narrower while overlapping the rabbit).
    for (const segText of TITLE_LINES) {
      const prepared = prepareWithSegments(segText, fontSpec, {
        letterSpacing: letterSpacingPx,
      });
      let cursor = { segmentIndex: 0, graphemeIndex: 0 };

      for (let safety = 0; safety < 20; safety++) {
        const lineBottom = y + lineHeight;
        const overlapsLogo = y < logoHeight && lineBottom > 0;
        const maxWidth = overlapsLogo
          ? Math.max(60, containerWidth - logoTotal)
          : containerWidth;

        const range = layoutNextLineRange(prepared, cursor, maxWidth);
        if (!range) break;
        const line = materializeLineRange(prepared, range);
        lines.push({
          text: line.text,
          maxWidth,
          measuredWidth: line.width,
          y,
          overlapsLogo,
        });
        if (
          range.end.segmentIndex === cursor.segmentIndex &&
          range.end.graphemeIndex === cursor.graphemeIndex
        ) {
          y += lineHeight;
          break;
        }
        cursor = range.end;
        y += lineHeight;
      }
    }

    const totalHeight = Math.max(y, logoHeight);

    return {
      fontSize,
      lineHeight,
      letterSpacingEm,
      logoWidth,
      logoHeight,
      logoGutter,
      totalHeight,
      lines,
    };
  }, [containerWidth, fontsReady]);
}

function SiteNav(props) {
  const path = pageForPath(
    typeof window !== "undefined" ? window.location.pathname : "/"
  );
  const currentId = path ? path.id : "";

  return (
    <header className="site-header">
      {props.includeWordmark ? (
        <a href="/" className="wordmark wordmark-link">
          Wonderland Software
        </a>
      ) : null}
      <nav className="site-nav" aria-label="Primary">
        {PAGES.map((item) => (
          <a
            key={item.id}
            href={item.href}
            aria-current={item.id === currentId ? "page" : undefined}
          >
            {item.id === "home"
              ? "Home"
              : item.id === "about"
                ? "About"
                : item.id === "vibrate"
                  ? "Vibrate"
                  : "Contact"}
          </a>
        ))}
      </nav>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      {FOOTER_LINKS.map((link) => (
        <a
          key={link.href}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={link.ariaLabel}
        >
          {link.label}
        </a>
      ))}
    </footer>
  );
}

function HomeHero() {
  const fontsReady = useFontsReady();
  const titleRef = useRef(null);
  const titleWidth = useElementWidth(titleRef);
  const layout = useWrappedTitle(titleWidth, fontsReady);
  const logoWidth = layout ? layout.logoWidth : VIBRATE_LOGO_W;
  const logoHeight = layout ? layout.logoHeight : VIBRATE_LOGO_H;

  return (
    <>
      <div className="wordmark">
        Wonderland Software
        <span className="wordmark-location">Austin, Texas</span>
      </div>

      <h1
        ref={titleRef}
        className="hero-title"
        aria-label={TITLE_TEXT}
        style={
          layout
            ? {
                height: layout.totalHeight,
                fontSize: layout.fontSize,
                lineHeight: `${layout.lineHeight}px`,
                letterSpacing: `${layout.letterSpacingEm}em`,
              }
            : { minHeight: logoHeight }
        }
      >
        <div
          className="rabbit-abs"
          style={{
            width: logoWidth,
            height: logoHeight,
          }}
        >
          <picture>
            <source type="image/webp" srcSet={vibrateLogoWebp} />
            <img
              src={vibrateLogoPng}
              alt="Vibrate logo"
              width={Math.round(logoWidth)}
              height={Math.round(logoHeight)}
            />
          </picture>
        </div>
        {layout ? (
          <>
            {layout.lines.map((line, i) => (
              <div
                key={i}
                className="title-line"
                aria-hidden="true"
                style={{
                  top: line.y,
                  width: line.maxWidth,
                }}
              >
                {line.text}
              </div>
            ))}
          </>
        ) : (
          <span className="title-fallback">{TITLE_TEXT}</span>
        )}
      </h1>
    </>
  );
}

function HomePage() {
  return (
    <>
      <HomeHero />
      <p className="hero-note">
        <a href="/vibrate/">About the Vibrate app</a>
      </p>

      <div className="clients">
        <p className="clients-label">Trusted by</p>
        <div className="clients-logos">
          {CLIENTS.map((c) => (
            <img key={c.name} src={c.logo} alt={c.name} className="client-logo" />
          ))}
        </div>
      </div>

      <p className="footer-note">
        Wonderland Software is a custom software studio based in Austin,
        Texas, building web apps, mobile apps, AI agents, and Shopify
        storefronts for teams here and anywhere else.
      </p>

      <ol className="services">
        <li className="service">
          <span className="service-num">01</span>
          <div className="service-body">
            <h2 className="service-title">Strategy Conversations.</h2>
            <p>
              Before anything gets built, we have a conversation. We sit down
              with you to map what your business actually needs, and what it
              doesn't.
            </p>
          </div>
        </li>

        <li className="service">
          <span className="service-num">02</span>
          <div className="service-body">
            <h2 className="service-title">Design &amp; Development.</h2>
            <p>
              Web apps, mobile apps, and custom tools built to fit your
              business. Thoughtfully designed, cleanly engineered, and shaped
              around the way your team actually works.
            </p>
          </div>
        </li>

        <li className="service">
          <span className="service-num">03</span>
          <div className="service-body">
            <h2 className="service-title">Integration &amp; Deployment.</h2>
            <p>
              From proof-of-concept to production, wired into the systems
              and workflows you already rely on.
            </p>
          </div>
        </li>
      </ol>
    </>
  );
}

function AboutPage() {
  return (
    <>
      <h1 className="page-title">Tag Locklar</h1>
      <div className="service-body">
        <p>Tag Locklar is the founder of Wonderland Software.</p>
      </div>
    </>
  );
}

function VibratePage() {
  return (
    <>
      <h1 className="page-title">Vibrate</h1>
      <picture className="product-mark">
        <source type="image/webp" srcSet={vibrateLogoWebp} />
        <img
          src={vibrateLogoPng}
          alt="Vibrate logo"
          width="88"
          height="77"
        />
      </picture>
      <div className="service-body">
        <p>
          Share your current vibe with a place pin, ratings, and friends.
        </p>
        <p>Vibrate is an iOS app from Wonderland Software. It is on TestFlight.</p>
      </div>
      <div className="page-actions">
        <a
          href="https://vibrate.world"
          className="contact-btn"
          target="_blank"
          rel="noopener noreferrer"
        >
          visit vibrate.world
          <span className="contact-arrow" aria-hidden="true">→</span>
        </a>
      </div>
    </>
  );
}

function ContactPage() {
  return (
    <>
      <h1 className="page-title">Contact</h1>
      <div className="service-body">
        <p>Austin, TX</p>
      </div>
      <div className="page-actions">
        <a href={MAILTO} className="contact-btn">
          email tag@wonderland.software
          <span className="contact-arrow" aria-hidden="true">→</span>
        </a>
      </div>
    </>
  );
}

function MissingPage() {
  return (
    <>
      <h1 className="page-title">Page not found.</h1>
      <div className="service-body">
        <p>That page does not exist.</p>
      </div>
    </>
  );
}

const PAGE_VIEWS = {
  home: HomePage,
  about: AboutPage,
  vibrate: VibratePage,
  contact: ContactPage,
};

function App() {
  const reducedMotion = usePrefersReducedMotion();
  useAnimatedFavicon(reducedMotion);
  const page = pageForPath(
    typeof window !== "undefined" ? window.location.pathname : "/"
  );
  usePageMeta(page);
  const View = page ? PAGE_VIEWS[page.id] : MissingPage;

  return (
    <>
      <BackgroundShader />
      <BrushOverlay reducedMotion={reducedMotion} />
      <div className="App">
        <article className={page && page.id === "home" ? "page page-home" : "page"}>
          <SiteNav includeWordmark={!page || page.id !== "home"} />
          <View />
          <SiteFooter />
        </article>
      </div>
    </>
  );
}

export default App;
